import { Dialog } from '@headlessui/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HiOutlineExclamation } from 'react-icons/hi';
import { useNavigate } from 'react-router';
import { Button, LinkButton, Modal } from '@src/components';
import PresignedDownloadButton from '@src/components/PresignedDownloadButton';
import {
  GetOrdersDocument,
  NifOrderRowFragment,
  useDeleteNifOrderMutation,
} from '@src/generated/graphql';
import { useRequiredUserId } from '@src/hooks/useRequiredUserId';
import { useToast } from '@src/hooks/useToast';
import { logAndFormatError } from '@src/services/error-formatter';
import { OrderStatusInfo } from '../../utils/order-status';
import { OrderItem } from './OrderItem';

function DeleteOrderButton({
  className,
  order,
}: {
  className?: string;
  order: NifOrderRowFragment;
}): JSX.Element {
  const userId = useRequiredUserId();
  const [deleteNif] = useDeleteNifOrderMutation({
    refetchQueries: [{ query: GetOrdersDocument, variables: { userId } }],
    awaitRefetchQueries: true,
  });
  const toast = useToast();
  const { t } = useTranslation('nif');

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isOrderDeleting, setIsOrderDeleting] = useState(false);

  const confirmOrderDeletion = async (): Promise<void> => {
    try {
      // enable disabled states
      setIsOrderDeleting(true);

      await deleteNif({
        variables: {
          id: order.id,
        },
      });

      setShowDeleteModal(false);
      toast.success(
        t('dashboard.nif_order_deleted', 'The NIF order was deleted'),
      );
    } catch (err) {
      toast.error(
        logAndFormatError(
          err,
          t('dashboard.nif_order_delete_error', 'Error deleting NIF order.'),
        ),
      );
    } finally {
      // restore states
      setIsOrderDeleting(false);
    }
  };

  return (
    <>
      <LinkButton
        className={className}
        onClick={() => setShowDeleteModal(true)}
      >
        {t('buttons.delete', 'Delete', { ns: 'translation' })}
      </LinkButton>
      <Modal
        isOpen={showDeleteModal}
        // Prevents closing outside dialog while deleting
        onClose={() => (isOrderDeleting ? null : setShowDeleteModal(false))}
      >
        <div className="flex">
          <div className="mx-auto flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-100">
            <HiOutlineExclamation
              className="h-6 w-6 text-red-600"
              aria-hidden="true"
            />
          </div>

          <div className="pl-3">
            <Dialog.Title
              as="h3"
              className="text-lg font-medium leading-6 text-gray-900"
            >
              {t('dashboard.delete_order.title', 'Delete order')}
            </Dialog.Title>

            <div className="mt-2">
              <div className="text-sm text-gray-500">
                {t(
                  'dashboard.delete_order.description',
                  'Are you sure you want to delete this order? This action cannot be undone.',
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-5 text-right">
          <Button
            color="white"
            className="justify-center"
            onClick={() => setShowDeleteModal(false)}
            disabled={isOrderDeleting}
          >
            {t('translation:buttons.cancel', 'Cancel')}
          </Button>
          <Button
            color="red"
            className="ml-3 justify-center"
            onClick={confirmOrderDeletion}
            disabled={isOrderDeleting}
          >
            {t('translation:buttons.delete', 'Delete')}
          </Button>
        </div>
      </Modal>
    </>
  );
}

function NifOrderActions({
  order,
}: {
  order: NifOrderRowFragment;
}): JSX.Element | null {
  const navigate = useNavigate();
  const { t } = useTranslation(['nif', 'translation']);
  switch (order.status) {
    case 'DRAFT':
      return (
        <div className="flex flex-row md:flex-row-reverse">
          <Button
            className="mr-8 md:mr-0"
            onClick={() => navigate(`/orders/nif/edit/${order.id}`)}
          >
            {t('buttons.continue', 'Continue', { ns: 'translation' })}
          </Button>
          <DeleteOrderButton order={order} className="md:mr-8" />
        </div>
      );
    case 'AWAITING_INTAKE':
      return (
        <Button
          onClick={() => navigate(`/orders/nif/reupload-intake/${order.id}`)}
        >
          {t('dashboard.update_documents_button', 'Update my documents')}
        </Button>
      );
    case 'POA_PENDING':
      return (
        <div className="flex flex-row">
          <Button
            onClick={() => navigate(`/orders/nif/upload-poa/${order.id}`)}
          >
            {t('dashboard.sign_poa_button', 'Sign Power of Attorney')}
          </Button>
        </div>
      );
    case 'NIF_RECEIVED':
      return (
        <div className="flex flex-row">
          {order.nifDocumentFile && (
            <PresignedDownloadButton fileId={order.nifDocumentFile.id}>
              {t('dashboard.download_nif_button', 'Download NIF')}
            </PresignedDownloadButton>
          )}
        </div>
      );
    default:
      return null;
  }
}

function NifInfo({ order }: { order: NifOrderRowFragment }): JSX.Element {
  return (
    <div className="flex flex-col space-y-1">
      <span className="font-bold">NIF</span>
      <span>{order.nifNumber}</span>
    </div>
  );
}

export function NifOrderItem({
  order,
  statusInfo,
}: {
  order: NifOrderRowFragment;
  statusInfo: OrderStatusInfo;
}): JSX.Element {
  const { t } = useTranslation('nif');

  return (
    <OrderItem
      title={t('dashboard.order_title', 'NIF for {{name}}', {
        name: `${order.givenNames} ${order.surname}`,
      })}
      statusInfo={statusInfo}
      actions={<NifOrderActions order={order} />}
    >
      {order.status === 'NIF_RECEIVED' && order.nifNumber && (
        <NifInfo order={order} />
      )}
    </OrderItem>
  );
}
